import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

import {
  FHTimer,
  FHButton,
  FHConfirmDialog,
  FHTextarea,
  FHWaveLoader,
} from '../../base';
import ErrorAlertIcon from '../../svgs/ErrorAlertIcon';
import LogoIcon from '../../svgs/LogoIcon';
import { NARRATIVE_AI_ALLOWED_FAILURE_COUNT } from '../../../pages/session/btSessionNotes/constants';
import { MINIMUM_REQUIRED_SENTENCE_LENGTH } from '../../../constants';
import { FORTA_BLUE, FORTA_ERROR, FORTA_WHITE } from '../../../utils/colors';
import { getNarrativeSuggestionContextKey } from '../../../utils/narrativeSuggestion';

import type { ChangeEvent } from 'react';
import type { NarrativeSuggestionContext } from '../../../utils/narrativeSuggestion';
import type { ISupervisionSessionNarrativeProps } from './types';
import type { NarrativeUpdateContext } from './types';

function SupervisionSessionNarrative({
  dataTestId,
  noteType,
  isEditable,
  noteId,
  narrative,
  sourceKey,
  textareaSx,
  showError = false,
  narrativeSuggestionLoading,
  onSuggest,
  onUpdate,
}: ISupervisionSessionNarrativeProps) {
  const [value, setValue] = useState<string>(narrative || '');
  const [generatingSuggestions, setGeneratingSuggestions] = useState<boolean>(false);
  const [disableGenerate, setDisableGenerate] = useState<boolean>(false);
  const [failureCount, setFailureCount] = useState<number>(0);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [acceptingContextKey, setAcceptingContextKey] = useState<string | null>(null);
  const [unsavedSuggestion, setUnsavedSuggestion] = useState<string>('');
  // The saved narrative at the failed attempt. Retry is offered only while it is unchanged.
  const [unsavedSuggestionBase, setUnsavedSuggestionBase] = useState<string>('');
  const [disclaimer, setDisclaimer] = useState<string>(
    'Forta AI suggested the following session narrative. You must review and edit the content to ensure accuracy and completeness.',
  );
  const hasLocalEditsRef = useRef<boolean>(false);
  const previousNoteIdRef = useRef<number | undefined>(noteId);
  const uneditedAiDraftRef = useRef<{ narrative: string; contextKey: string } | null>(null);
  const latestContextRef = useRef<{ key: string; version: number } | null>(null);
  const latestRequestRef = useRef<number>(0);
  const acceptingContextKeyRef = useRef<string | null>(null);

  const aiSuggestionAllowed = isEditable && Boolean(noteId);
  const context: NarrativeUpdateContext = {
    noteId: noteId ?? 0,
    noteType,
    sourceKey: sourceKey ?? '',
    contextToken: '',
  };
  const contextKey = getNarrativeSuggestionContextKey(context);
  context.contextToken = contextKey;
  const isAcceptingSuggestion = acceptingContextKey === contextKey;

  const isCurrentRequest = (
    requestedKey: string,
    requestedVersion: number,
    requestedRequest: number,
  ): boolean =>
    latestContextRef.current?.key === requestedKey &&
    latestContextRef.current.version === requestedVersion &&
    latestRequestRef.current === requestedRequest;

  useEffect(() => {
    if (latestContextRef.current?.key === contextKey) return;
    latestContextRef.current = {
      key: contextKey,
      version: (latestContextRef.current?.version ?? 0) + 1,
    };
    latestRequestRef.current += 1;
  }, [contextKey]);

  useEffect(() => {
    if (hasLocalEditsRef.current) return;
    setValue(narrative || '');
  }, [narrative]);

  useEffect(() => {
    const noteChanged = previousNoteIdRef.current !== noteId;
    previousNoteIdRef.current = noteId;
    if (noteChanged) {
      hasLocalEditsRef.current = false;
      setValue(narrative || '');
    }
    setGeneratingSuggestions(false);
    uneditedAiDraftRef.current = null;
    setUnsavedSuggestion('');
    setFailureCount(0);
    setDisableGenerate(false);
    setDisclaimer(
      'Forta AI suggested the following session narrative. You must review and edit the content to ensure accuracy and completeness.',
    );
    acceptingContextKeyRef.current = null;
    setAcceptingContextKey(null);
    // The context key is the complete note/source identity. Narrative changes are
    // intentionally not dependencies because saves must not trigger another check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextKey]);

  const handleGenerateNarrative = async () => {
    if (
      !aiSuggestionAllowed ||
      narrativeSuggestionLoading ||
      generatingSuggestions ||
      isAcceptingSuggestion
    ) {
      return;
    }

    if (!noteId) return;
    setUnsavedSuggestion('');
    setGeneratingSuggestions(true);
    const requestedContext = {
      noteId,
      noteType,
      sourceKey: sourceKey ?? '',
    } satisfies NarrativeSuggestionContext;
    const requestedContextKey = getNarrativeSuggestionContextKey(requestedContext);
    const requestedContextVersion = latestContextRef.current?.version ?? 0;
    const requestedRequest = latestRequestRef.current + 1;
    latestRequestRef.current = requestedRequest;
    try {
      const result = await onSuggest(requestedContext);
      if (!isCurrentRequest(requestedContextKey, requestedContextVersion, requestedRequest)) return;
      if (result?.error) {
        setDisclaimer(
          failureCount >= 2
            ? 'Forta AI is currently unavailable. Please proceed by writing your narrative.'
            : 'Forta AI is having trouble generating a session narrative.',
        );
        setDisableGenerate(true);
        setFailureCount((previous) => previous + 1);
        return;
      }

      const newNarrative = result?.payload?.narrative?.trim() || '';
      setFailureCount(0);
      setDisableGenerate(false);
      setDisclaimer(
        'Forta AI suggested the following session narrative. You must review and edit the content to ensure accuracy and completeness.',
      );
      // The replace warning was shown before the request, so apply the text now.
      uneditedAiDraftRef.current = { narrative: newNarrative, contextKey: requestedContextKey };
      await handleAcceptAiDraft();
    } finally {
      if (isCurrentRequest(requestedContextKey, requestedContextVersion, requestedRequest)) {
        setGeneratingSuggestions(false);
      }
    }
  };

  const handleEdit = (event: ChangeEvent<HTMLTextAreaElement>) => {
    hasLocalEditsRef.current = true;
    uneditedAiDraftRef.current = null;
    setUnsavedSuggestion('');
    setValue(event.target.value);
  };

  const handleUpdate = () => {
    hasLocalEditsRef.current = false;
    const nextNarrative = value.trim();

    if (narrative === nextNarrative) {
      uneditedAiDraftRef.current = null;
      return;
    }
    const uneditedAiDraft = uneditedAiDraftRef.current;
    if (
      uneditedAiDraft?.contextKey === contextKey &&
      uneditedAiDraft.narrative === nextNarrative
    ) {
      // Generated text is a local preview. It is persisted only by the
      // explicit acceptance action below.
      return;
    }
    onUpdate(nextNarrative);
    uneditedAiDraftRef.current = null;
  };

  const handleAcceptAiDraft = async () => {
    if (acceptingContextKeyRef.current === contextKey) return;

    const pendingDraft = uneditedAiDraftRef.current;
    const baseNarrative = narrative ?? '';
    const acceptedNarrative = pendingDraft?.narrative.trim() ?? '';
    const requestedContextVersion = latestContextRef.current?.version ?? 0;
    const requestedRequest = latestRequestRef.current;

    if (
      !acceptedNarrative ||
      !pendingDraft ||
      pendingDraft.contextKey !== contextKey ||
      pendingDraft.narrative !== acceptedNarrative
    ) {
      return;
    }

    acceptingContextKeyRef.current = contextKey;
    setAcceptingContextKey(contextKey);
    try {
      const updateResult = await onUpdate(acceptedNarrative, acceptedNarrative, context);
      if (!isCurrentRequest(contextKey, requestedContextVersion, requestedRequest)) {
        return;
      }
      if (updateResult !== true) {
        setValue(narrative || '');
        if (updateResult === 'conflict') {
          // The note changed on the server, so this suggestion is stale. Do not offer a retry.
          uneditedAiDraftRef.current = null;
          setUnsavedSuggestion('');
          return;
        }
        // Keep the suggestion so the clinician can save it again without a new request.
        setUnsavedSuggestion(acceptedNarrative);
        setUnsavedSuggestionBase(baseNarrative);
        return;
      }

      setUnsavedSuggestion('');
      setValue(acceptedNarrative);
      uneditedAiDraftRef.current = null;
    } finally {
      if (acceptingContextKeyRef.current === contextKey) {
        acceptingContextKeyRef.current = null;
        setAcceptingContextKey(null);
      }
    }
  };

  const handleRetrySave = () => {
    if (!unsavedSuggestion || (narrative ?? '') !== unsavedSuggestionBase) return;
    uneditedAiDraftRef.current = { narrative: unsavedSuggestion, contextKey };
    void handleAcceptAiDraft();
  };

  const onGenerate = () => {
    if (narrativeSuggestionLoading || generatingSuggestions || isAcceptingSuggestion) return;
    if (value.trim()) {
      setOpenConfirm(true);
      return;
    }
    void handleGenerateNarrative();
  };

  const handleRetryTimerComplete = () => {
    setDisableGenerate(false);
    setDisclaimer("Select 'Suggest Narrative' to prompt Forta AI to generate a session narrative");
  };

  return (
    <>
      {disclaimer && aiSuggestionAllowed && (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 1, mb: 1 }}>
          <ErrorAlertIcon color={FORTA_ERROR} />
          <Typography textAlign="left" color={FORTA_ERROR} ml={1}>
            {disclaimer}
          </Typography>
        </Box>
      )}
      <Box sx={{ flexGrow: 1, position: 'relative' }}>
        <FHTextarea
          data-testid={dataTestId}
          sx={{ borderRadius: '8px', boxShadow: 'none', ...textareaSx }}
          fullWidth
          expandable={isEditable}
          placeholder="Describe the session and client's response"
          minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
          disabled={
            !isEditable ||
            narrativeSuggestionLoading ||
            generatingSuggestions ||
            isAcceptingSuggestion
          }
          value={value}
          onEdit={handleEdit}
          onBlur={handleUpdate}
          onCloseExpandDialog={handleUpdate}
          error={
            isEditable &&
            value.trim().length < MINIMUM_REQUIRED_SENTENCE_LENGTH &&
            (Boolean(value.trim()) || showError)
          }
          helperText={isEditable ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters` : ''}
        />
        {generatingSuggestions && (
          <Box
            sx={{
              backgroundColor: FORTA_WHITE,
              position: 'absolute',
              top: 0,
              width: '100%',
              height: '90%',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <FHWaveLoader text="Forta AI is generating your session narrative" />
          </Box>
        )}
      </Box>
      {unsavedSuggestion && aiSuggestionAllowed && (narrative ?? '') === unsavedSuggestionBase && (
        <Box
          data-testid={`${dataTestId}-unsaved-suggestion`}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}
        >
          <Typography color={FORTA_ERROR}>The suggested narrative was not saved.</Typography>
          <FHButton
            data-testid={`${dataTestId}-retry-save-button`}
            disabled={isAcceptingSuggestion}
            onClick={handleRetrySave}
          >
            {isAcceptingSuggestion ? 'Saving...' : 'Retry save'}
          </FHButton>
        </Box>
      )}
      {aiSuggestionAllowed && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <FHButton
            data-testid={`${dataTestId}-suggest-button`}
            sx={{
              mt: 3,
              backgroundColor: FORTA_WHITE,
              borderWidth: 1,
              borderStyle: 'solid',
              color: FORTA_BLUE.primary,
              borderColor: FORTA_BLUE.light2,
              ':hover': { color: FORTA_BLUE.primary, backgroundColor: FORTA_WHITE },
              ':disabled': {
                color: FORTA_BLUE.primary,
                backgroundColor: FORTA_WHITE,
                opacity: 0.7,
                border: 'none',
              },
            }}
            disabled={
              narrativeSuggestionLoading ||
              generatingSuggestions ||
              isAcceptingSuggestion ||
              disableGenerate ||
              failureCount >= NARRATIVE_AI_ALLOWED_FAILURE_COUNT
            }
            onClick={onGenerate}
            startIcon={<LogoIcon color={FORTA_BLUE.primary} />}
          >
            {disableGenerate && failureCount < NARRATIVE_AI_ALLOWED_FAILURE_COUNT ? (
              <Box sx={{ width: '130px', display: 'flex' }}>
                <Typography mr={1}>Retry in: </Typography>
                <FHTimer duration={60} onTimerComplete={handleRetryTimerComplete} />
              </Box>
            ) : (
              'Suggest Narrative'
            )}
          </FHButton>
        </Box>
      )}
      <FHConfirmDialog
        open={openConfirm}
        title="Suggest Narrative with Forta AI"
        titleStyles={{ color: FORTA_BLUE.primary }}
        dialogStyles={{
          padding: '16px',
          maxHeight: '85vh',
          height: 'auto',
          width: '580px',
          maxWidth: '580px',
        }}
        sx={{
          '& .MuiModal-backdrop': {
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
          },
        }}
        contentRenderer={() => (
          <Box px={2} py={4}>
            <Typography display="inline">
              Generating a new narrative will erase the current session narrative you have already
              input. Do you wish to continue?
            </Typography>
          </Box>
        )}
        submittext="Continue"
        canceltext="Cancel"
        onClose={(_, reason) => {
          if (reason && reason === 'backdropClick') return;
          setOpenConfirm(false);
        }}
        onSubmit={() => {
          setOpenConfirm(false);
          void handleGenerateNarrative();
        }}
      />
    </>
  );
}

export default SupervisionSessionNarrative;
